import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { mediaList } from '@/mocks/media';
import { newsList } from '@/mocks/news';

const features = [
  {
    icon: 'ri-film-line',
    title: '丰富影视库',
    desc: '涵盖电影与电视剧两大频道，类型覆盖动作、科幻、悬疑、都市、古装等，满足不同口味的观影需求。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '多维筛选',
    desc: '支持按类型、地区、年份范围组合筛选，并提供更新时间、热度、评分三种排序方式，快速定位目标内容。',
  },
  {
    icon: 'ri-search-line',
    title: '便捷搜索',
    desc: '可通过影片名称、导演或演员关键词直接搜索，搜索结果与筛选列表打通，查找更高效。',
  },
  {
    icon: 'ri-newspaper-line',
    title: '权威资讯',
    desc: '持续整理影讯速递、新片推荐与行业动态，帮助你了解影视热点与市场趋势。',
  },
];

const team = [
  { role: '内容策划', name: '影视编辑部', desc: '负责影视资料整理、分类维护与资讯撰写' },
  { role: '产品设计', name: '体验设计组', desc: '负责网站信息架构、交互流程与视觉设计' },
  { role: '技术开发', name: '前端工程组', desc: '负责网站功能开发与性能优化' },
];

export default function About() {
  useSeo({
    title: '关于我们 - 天堂影视',
    description:
      '天堂影视是面向影视爱好者的内容平台，提供最新电影、电视剧的浏览与资讯服务，致力于打造专业、好用、内容丰富的影视信息入口。',
    keywords: '天堂影视,关于我们,影视平台介绍,联系我们',
  });

  const stats = [
    { value: `${mediaList.length * 120}+`, label: '收录影视' },
    { value: `${newsList.length * 30}+`, label: '累计资讯' },
    { value: '4', label: '内容分类' },
    { value: '24h', label: '持续更新' },
  ];

  return (
    <div>
      <section className="bg-primary-950 py-12 text-background-50 md:py-16">
        <div className="container-page">
          <nav className="flex items-center gap-2 text-sm text-background-200/80">
            <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="mt-5 text-3xl font-bold md:text-4xl">关于天堂影视</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200/85 md:text-base">
            天堂影视是一个面向影视爱好者的内容平台。我们希望用清晰的信息架构与友好的浏览体验，
            帮助每一位观众更轻松地发现值得观看的电影与电视剧，并及时了解影视行业的最新动态。
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50/10 p-4 backdrop-blur">
                <p className="text-2xl font-bold text-accent-400">{s.value}</p>
                <p className="mt-1 text-sm text-background-200/80">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">网站定位</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              天堂影视定位为「内容发现型」影视门户。与单纯的资源聚合不同，我们更关注用户如何高效地
              找到想看的内容：通过分类频道、多维度筛选、关键词搜索与相关推荐，帮助用户在有限的时间里
              做出更合适的选择。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              同时，我们重视内容的专业性与可信度，资讯频道的每一篇内容都围绕真实的行业话题展开，
              力求为观众提供有价值的背景信息与观察视角。
            </p>

            <h2 className="mt-10 text-xl font-bold text-foreground-950 md:text-2xl">官方声明</h2>
            <div className="mt-4 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <ul className="space-y-3 text-sm leading-relaxed text-secondary-900">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                  本站当前为演示站点，所有影视资料、评分、人物信息与资讯内容均为虚构示例数据。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                  在线播放功能为前端演示状态，正式播放接口待接入，不会真正加载任何视频资源。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                  本站不提供任何实际影视文件下载或存储服务，如涉及版权问题请与我们联系。
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
            <div className="mt-4 space-y-3">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'contact@paradisetv.example' },
                { icon: 'ri-phone-line', label: '客服电话', value: '400-000-6688' },
                { icon: 'ri-time-line', label: '服务时间', value: '周一至周五 09:00 - 18:00' },
                {
                  icon: 'ri-map-pin-line',
                  label: '办公地址',
                  value: '浙江省杭州市滨江区影视产业园 A 座 1201',
                },
              ].map((c) => (
                <div
                  key={c.label}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-400">{c.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-foreground-800">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12">
        <div className="container-page">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">我们提供什么</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className={`${f.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-12">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">团队与版权</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {team.map((t) => (
            <div key={t.role} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="text-xs font-medium text-accent-600">{t.role}</p>
              <p className="mt-1 text-base font-semibold text-foreground-950">{t.name}</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm leading-relaxed text-foreground-500">
          © 2025 天堂影视 Paradise TV. 保留所有权利。本站所有内容均为演示数据，仅供展示与学习使用。
        </p>
      </section>
    </div>
  );
}